// Notification utility for browser notifications
import moment from 'moment';

class NotificationService {
  constructor() {
    this.permission = null;
    this.checkPermission();
  }

  checkPermission() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
    return this.permission;
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      return {
        success: false,
        error: 'This browser does not support notifications'
      };
    }

    if (this.permission === 'granted') {
      return { success: true, permission: 'granted' };
    }

    if (this.permission === 'denied') {
      return {
        success: false,
        error: 'Notifications are blocked. Please enable them in your browser settings.'
      };
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        return { success: true, permission: 'granted' };
      } else {
        return {
          success: false,
          error: 'Notification permission denied'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Error requesting notification permission: ' + error.message
      };
    }
  }

  show(title, options = {}) {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return null;
    }

    if (this.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'default',
        requireInteraction: options.requireInteraction || false,
        ...options
      });

      // Auto-close after 5 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, options.duration || 5000);
      }

      // Handle click
      if (options.onClick) {
        notification.onclick = options.onClick;
      } else {
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  showGigReminder(gig) {
    return this.show(`Upcoming Gig: ${gig.title}`, {
      body: `Starts: ${new Date(gig.start_date).toLocaleDateString()}\n${gig.venue || 'No venue'}`,
      tag: `gig-${gig.id}`,
      requireInteraction: false,
      onClick: () => {
        window.focus();
      }
    });
  }

  showUpcomingGigsSummary(count) {
    return this.show(`You have ${count} upcoming gig${count > 1 ? 's' : ''}`, {
      body: `Check your calendar for details`,
      tag: `upcoming-summary`,
      requireInteraction: false,
      onClick: () => {
        window.focus();
      }
    });
  }

  showPaymentReminder(gig, timeAgo = '') {
    const timeText = timeAgo ? ` (ended ${timeAgo})` : '';
    const days = gig.end_date && gig.start_date ? 
      moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1 : 1;
    const rateType = gig.rate_type || 'daily';
    const amount = rateType === 'flat' ? 
      parseFloat(gig.daily_rate || 0) : 
      days * parseFloat(gig.daily_rate || 0);
    
    return this.show(`Payment Reminder: ${gig.title}${timeText}`, {
      body: `This gig is unpaid. Amount: €${amount.toFixed(2)}`,
      tag: `payment-${gig.id}-${timeAgo}`,
      requireInteraction: false,
      onClick: () => {
        window.focus();
      }
    });
  }

  showPaymentReminderSummary(unpaidGigs) {
    const totalAmount = unpaidGigs.reduce((sum, gig) => {
      const days = gig.end_date && gig.start_date ? 
        moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1 : 1;
      const rateType = gig.rate_type || 'daily';
      const amount = rateType === 'flat' ? 
        parseFloat(gig.daily_rate || 0) : 
        days * parseFloat(gig.daily_rate || 0);
      return sum + amount;
    }, 0);

    return this.show(`Payment Reminder: ${unpaidGigs.length} unpaid gig${unpaidGigs.length > 1 ? 's' : ''}`, {
      body: `Total amount: €${totalAmount.toFixed(2)}\nCheck unpaid gigs list`,
      tag: `payment-summary`,
      requireInteraction: false,
      onClick: () => {
        window.focus();
      }
    });
  }

  showSuccess(message) {
    return this.show('Success', {
      body: message,
      tag: 'success',
      requireInteraction: false
    });
  }

  showError(message) {
    return this.show('Error', {
      body: message,
      tag: 'error',
      requireInteraction: false
    });
  }

  showInfo(message) {
    return this.show('Information', {
      body: message,
      tag: 'info',
      requireInteraction: false
    });
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;

